/**
 * Live preview canvas — landing-sections のレジストリでセクションを順次レンダリング。
 * viewport (desktop/tablet/mobile) は max-width で切替。
 * 選択中セクションは青枠ハイライト。
 */
import { useMemo } from 'react';

import { Spinner } from '@/components/ui/spinner';
import { type LandingRecord, loadLandingSections } from '@/hooks/useLandings';
import { useQuery } from '@tanstack/react-query';

import {
  SECTIONS,
  type SectionType,
  type VisualIdentity,
} from '@goenhub/landing-sections';
import { identityVars } from '@goenhub/landing-sections/identities';

import { enrichContentWithFallbackCta, getFallbackCtaHref } from '../utils/ctaFallback';

interface Props {
  landingId: string;
  landing: LandingRecord;
  activeSectionId: string | null;
  viewport: 'desktop' | 'tablet' | 'mobile';
  onSelectSection: (id: string) => void;
}

export function PreviewCanvas({ landingId, landing, activeSectionId, viewport, onSelectSection }: Props) {
  const { data: sections, isLoading } = useQuery({
    queryKey: ['landings', landingId, 'sections', 'all'],
    queryFn: () => loadLandingSections(landingId),
  });

  const sortedRefs = useMemo(
    () => (landing.sections ?? []).slice().sort((a, b) => a.order - b.order),
    [landing.sections],
  );

  // 1:N 対応: フォールバック URL には campaignId を焼き込まない。
  // プレビューは URL クエリがないので、enrich 時 opts.campaignId に sourceCampaignId を渡す。
  const fallbackCtaHref = useMemo(
    () => getFallbackCtaHref(landing.assignedCampaigns),
    [landing.assignedCampaigns],
  );

  const widthMap = { desktop: 1280, tablet: 768, mobile: 375 } as const;
  const targetWidth = widthMap[viewport];

  const identityCss = identityVars(landing.visualIdentity as VisualIdentity, landing.brandSettings);
  const brandContext = useMemo(
    () => ({
      logoUrl: landing.brandSettings?.logoStoragePath ?? null,
      socialLinks: landing.brandSettings?.socialLinks ?? null,
    }),
    [landing.brandSettings],
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Spinner />
      </div>
    );
  }

  // 空状態（まだ生成していない）
  if (sortedRefs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center gap-3 p-8">
        <div className="text-4xl opacity-30">✨</div>
        <h2 className="text-base font-semibold">プレビューはまだありません</h2>
        <p className="text-sm text-[var(--text-muted)] max-w-md">
          左パネルでキャンペーンを選択し、ブランド名を入力したら<br />
          「ランディングを生成」をクリックしてください。
        </p>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* ブラウザクローム */}
      <div
        className="mx-auto rounded-lg border border-[var(--border)] overflow-hidden bg-white dark:bg-[#0B0D10] shadow-lg"
        style={{
          maxWidth: targetWidth,
          width: '100%',
          transition: 'max-width 0.2s',
        }}
      >
        <div className="flex items-center px-3 py-2 bg-[#f5f5f3] dark:bg-[#1A1E25] border-b border-[var(--border)]">
          <div className="flex gap-1.5">
            <span className="size-3 rounded-full bg-red-400" />
            <span className="size-3 rounded-full bg-yellow-400" />
            <span className="size-3 rounded-full bg-green-400" />
          </div>
          <div className="flex-1 text-center text-xs text-gray-500 font-mono">
            https://{landing.domain || 'example.com'}/{landing.slug}
          </div>
          <div className="text-xs text-gray-400">{targetWidth}px</div>
        </div>
        <div data-ls-identity={landing.visualIdentity} style={identityCss as React.CSSProperties}>
          {sortedRefs.map((ref) => {
            const sectionDoc = (sections ?? []).find((s) => s.id === ref.id);
            if (!sectionDoc) return null;
            const sectionDef = SECTIONS[ref.type as SectionType];
            if (!sectionDef) return null;
            const Comp = sectionDef.variants[ref.variant];
            if (!Comp) return null;
            const isActive = ref.id === activeSectionId;
            const isHidden = !!ref.hidden;
            const enriched = enrichContentWithFallbackCta(
              sectionDoc.content as Record<string, unknown>,
              fallbackCtaHref,
              landing.sourceCampaignId,
            );
            const wrapStyle: React.CSSProperties = {
              opacity: isHidden ? 0.4 : 1,
              transition: 'opacity 0.2s',
            };
            if (isActive) {
              wrapStyle.outline = '3px solid var(--ls-accent, #3D5AFE)';
              wrapStyle.outlineOffset = -3;
            }
            return (
              <div
                key={ref.id}
                className="relative cursor-pointer"
                onClick={() => onSelectSection(ref.id)}
                style={wrapStyle}
              >
                {isActive && (
                  <div
                    className="absolute top-2 right-2 z-10 px-2 py-0.5 rounded text-[10px] font-semibold bg-[var(--ls-accent,#3D5AFE)] text-white"
                    style={{ pointerEvents: 'none' }}
                  >
                    {ref.type} · {ref.variant}
                  </div>
                )}
                {isHidden && (
                  <div
                    className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-700 text-white"
                    style={{ pointerEvents: 'none' }}
                  >
                    非表示
                  </div>
                )}
                <Comp content={enriched as never} style={sectionDoc.style} brand={brandContext} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
